/**
 * Shared soft-delete helpers for question bank UIs.
 *
 * Soft-delete is always server-side (deleted_at / soft_deleted_at). These
 * helpers only call DELETE and manage multi-select checkbox state.
 */
import React, { useCallback, useState } from 'react';
import { v1Delete } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Gated } from '@/auth/permissions';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/** Compact checkbox sizing for question list rows (text-sm adjacent). */
const BANK_CHECKBOX_CLASS = 'h-3.5 w-3.5 [&_svg]:h-2.5 [&_svg]:w-2.5';

export function useCheckedQuestionIds() {
  const [checked, setChecked] = useState(() => new Set());

  const toggle = useCallback((id, on) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setChecked(new Set()), []);

  const setAll = useCallback((ids, on) => {
    setChecked(() => (on ? new Set(ids) : new Set()));
  }, []);

  return { checked, toggle, clear, setAll, count: checked.size };
}

export async function softDeleteQuestions(apiPath, ids) {
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return { ok: 0, failed: 0 };
  const results = await Promise.allSettled(
    unique.map((id) => v1Delete(`${apiPath}/${id}`)),
  );
  const ok = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.length - ok;
  return { ok, failed };
}

/**
 * Toolbar above the question list: select-all + delete selected.
 * Hidden entirely when the user lacks ``editPermission``.
 */
export function QuestionBankBulkDeleteBar({
  ids,
  checked,
  toggle,
  setAll,
  clear,
  apiPath,
  editPermission,
  onDeleted,
  testIdPrefix = 'bank',
}) {
  const selectedIds = ids.filter((id) => checked.has(id));
  const allSelected = ids.length > 0 && selectedIds.length === ids.length;

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const label = selectedIds.length === 1
      ? 'Delete 1 selected question?'
      : `Delete ${selectedIds.length} selected questions?`;
    if (!window.confirm(`${label} They will be removed from the bank but kept in the database.`)) {
      return;
    }
    const { ok, failed } = await softDeleteQuestions(apiPath, selectedIds);
    clear();
    onDeleted?.(selectedIds);
    if (failed === 0) toast.success(ok === 1 ? 'Question deleted' : `${ok} questions deleted`);
    else toast.error(`Deleted ${ok}, failed ${failed}`);
  };

  return (
    <Gated need={editPermission}>
      <div
        className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b px-1 pb-2"
        data-testid={`${testIdPrefix}-bulk-bar`}
      >
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(v) => setAll(ids, v === true)}
            className={BANK_CHECKBOX_CLASS}
            data-testid={`${testIdPrefix}-select-all`}
            aria-label="Select all questions"
          />
          Select all
        </label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-rose-700 hover:bg-rose-50"
          disabled={selectedIds.length === 0}
          onClick={deleteSelected}
          data-testid={`${testIdPrefix}-delete-selected`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete All{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
        </Button>
      </div>
    </Gated>
  );
}

/**
 * Checkbox that sits beside a list row without selecting the question.
 */
export function QuestionRowCheckbox({
  id, checked, onToggle, editPermission, testId,
}) {
  return (
    <Gated need={editPermission}>
      <div
        className="flex shrink-0 self-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onToggle(id, v === true)}
          className={BANK_CHECKBOX_CLASS}
          data-testid={testId}
          aria-label="Select question"
        />
      </div>
    </Gated>
  );
}

/**
 * Editor footer Delete button (soft-delete current question).
 */
export function QuestionEditorDeleteButton({
  apiPath, questionId, questionPreview, editPermission, onDeleted, testId,
}) {
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!questionId || busy) return;
    const preview = (questionPreview || '').slice(0, 60);
    if (!window.confirm(
      `Delete this question${preview ? `: "${preview}${preview.length >= 60 ? '…' : ''}"` : ''}? It will be removed from the bank but kept in the database.`,
    )) return;
    setBusy(true);
    try {
      await v1Delete(`${apiPath}/${questionId}`);
      toast.success('Question deleted');
      onDeleted?.(questionId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (e?.message || 'Could not delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Gated need={editPermission}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
        onClick={remove}
        disabled={busy}
        data-testid={testId}
      >
        <Trash2 className="h-3.5 w-3.5" /> {busy ? 'Deleting…' : 'Delete'}
      </Button>
    </Gated>
  );
}
